import { getProductRepository } from '../../commerce/product.repository';
import { cutoutUrl } from '../../commerce/cutouts';
import { resizedImageUrl } from '../../commerce/shopify.repository';
import type { ShopProduct } from '../../commerce/shopify.types';
import type { SceneHotspot, ShelfConfig } from '../../scenes/scene.types';

export interface Shelf {
  /** Grille des niches, posée dans le décor qui défile. */
  element: HTMLElement;
  /** Tri et pagination, fixes à l'écran : ils ne doivent pas défiler avec le mur. */
  controls: HTMLElement;
  destroy(): void;
}

interface PageState {
  /** Curseur de la page affichée, null pour la première. */
  cursor: string | null;
  /** Curseurs des pages précédentes, pour pouvoir revenir en arrière. */
  history: (string | null)[];
  hasNext: boolean;
}

function gridSize(config: ShelfConfig, viewport: number): { columns: number; rows: number } {
  // Les variantes mobiles ne s'appliquent qu'aux étagères d'une scène classique :
  // un panorama garde sa grille, puisqu'il se parcourt par défilement.
  const narrow = viewport < 768 && config.mobileColumns !== undefined;
  return {
    columns: narrow ? (config.mobileColumns ?? config.columns) : config.columns,
    rows: narrow ? (config.mobileRows ?? config.rows) : config.rows,
  };
}

function nicheCount(config: ShelfConfig, viewport: number): number {
  const { columns, rows } = gridSize(config, viewport);
  return columns * rows;
}

/**
 * Étagère alimentée par le catalogue. Chaque produit devient un hotspot
 * synthétique passé au même gestionnaire que les hotspots du décor : la fiche
 * produit, les analytics et l'état « déjà découvert » fonctionnent sans rien
 * dupliquer.
 */
export interface ShelfHandlers {
  onProduct: (hotspot: SceneHotspot, origine?: HTMLElement) => void;
  onHover?: (product: ShopProduct, anchor: HTMLElement) => void;
  onLeave?: () => void;
}

export function createShelf(config: ShelfConfig, handlers: ShelfHandlers): Shelf {
  const onProduct = handlers.onProduct;
  const element = document.createElement('section');
  element.className = 'shelf';
  element.style.left = `${config.x}%`;
  element.style.top = `${config.y}%`;
  element.style.width = `${config.width}%`;
  element.style.height = `${config.height}%`;
  element.setAttribute('aria-label', 'Étagères de la réserve');

  const toolbar = document.createElement('div');
  toolbar.className = 'shelf__toolbar';
  toolbar.setAttribute('role', 'group');
  toolbar.setAttribute('aria-label', 'Trier l’étagère');

  const grid = document.createElement('ul');
  grid.className = 'shelf__grid';

  function applyGridSize(): void {
    const { columns, rows } = gridSize(config, window.innerWidth);
    grid.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
    grid.style.gridTemplateRows = `repeat(${rows}, 1fr)`;
  }
  applyGridSize();

  const status = document.createElement('p');
  status.className = 'shelf__status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');

  const pager = document.createElement('div');
  pager.className = 'shelf__pager';

  const previous = document.createElement('button');
  previous.type = 'button';
  previous.className = 'shelf__page-button';
  previous.textContent = 'Étagère précédente';

  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'shelf__page-button';
  next.textContent = 'Étagère suivante';

  pager.append(previous, status, next);
  element.append(grid);

  const controls = document.createElement('div');
  controls.className = 'shelf__controls';
  controls.append(toolbar, pager);

  const first = config.collections[0];
  let current = first?.handle ?? '';
  let page: PageState = { cursor: null, history: [], hasNext: false };
  let requestId = 0;

  const tabs = new Map<string, HTMLButtonElement>();
  for (const collection of config.collections) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'shelf__tab';
    button.textContent = collection.label;
    button.addEventListener('click', () => {
      if (current === collection.handle) return;
      current = collection.handle;
      page = { cursor: null, history: [], hasNext: false };
      markActiveTab();
      void load();
    });
    tabs.set(collection.handle, button);
    toolbar.append(button);
  }

  function markActiveTab(): void {
    for (const [handle, button] of tabs) {
      const active = handle === current;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }
  }

  function renderProducts(products: ShopProduct[]): void {
    grid.replaceChildren();
    for (const product of products) {
      const item = document.createElement('li');
      item.className = 'shelf__niche';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'shelf__product';
      button.setAttribute('aria-label', `${product.title}, ${product.price.amount} euros`);
      if (!product.availableForSale) button.dataset['unavailable'] = 'true';

      if (product.imageUrl) {
        const image = document.createElement('img');
        // Vignette : douze images de 1000 px feraient exploser le budget.
        image.src = cutoutUrl(product.handle, resizedImageUrl(product.imageUrl, 240));
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.addEventListener('error', () => image.remove());
        button.append(image);
      }

      const caption = document.createElement('span');
      caption.className = 'shelf__caption';
      caption.textContent = product.title;
      button.append(caption);

      button.addEventListener('click', () =>
        onProduct(
          {
            id: `reserve.product.${product.handle}`,
            label: product.title,
            action: 'product',
            target: product.handle,
            x: 0,
            y: 0,
            width: 0,
            height: 0,
          },
          button,
        ),
      );

      const montrer = (): void => handlers.onHover?.(product, button);
      const cacher = (): void => handlers.onLeave?.();
      button.addEventListener('pointerenter', montrer);
      button.addEventListener('focus', montrer);
      button.addEventListener('pointerleave', cacher);
      button.addEventListener('blur', cacher);

      item.append(button);
      grid.append(item);
    }
  }

  async function load(): Promise<void> {
    const ticket = ++requestId;
    status.textContent = 'Chargement…';
    previous.disabled = true;
    next.disabled = true;

    try {
      const result = await getProductRepository().getCollectionProducts(
        current,
        nicheCount(config, window.innerWidth),
        page.cursor,
      );
      // Une réponse tardive ne doit pas écraser un tri demandé entre-temps.
      if (ticket !== requestId) return;

      page.hasNext = result.hasNextPage;
      renderProducts(result.products);

      const label = config.collections.find((item) => item.handle === current)?.label ?? '';
      status.textContent = result.products.length
        ? `${label} — page ${page.history.length + 1}`
        : `${label} — rayon vide pour le moment`;

      previous.disabled = page.history.length === 0;
      next.disabled = !result.hasNextPage;
      if (result.hasNextPage) next.dataset['cursor'] = result.endCursor ?? '';
    } catch {
      if (ticket !== requestId) return;
      grid.replaceChildren();
      status.textContent = 'La réserve ne répond pas. La boutique classique reste ouverte.';
      previous.disabled = page.history.length === 0;
      next.disabled = true;
    }
  }

  next.addEventListener('click', () => {
    if (!page.hasNext) return;
    page.history.push(page.cursor);
    page.cursor = next.dataset['cursor'] ?? null;
    void load();
  });

  previous.addEventListener('click', () => {
    const back = page.history.pop();
    page.cursor = back ?? null;
    void load();
  });

  const onResize = (): void => {
    // Le nombre de niches change avec le breakpoint : on recharge la page.
    applyGridSize();
    void load();
  };
  window.addEventListener('resize', onResize);

  markActiveTab();
  void load();

  return {
    element,
    controls,
    destroy(): void {
      requestId += 1;
      window.removeEventListener('resize', onResize);
      controls.remove();
      element.remove();
    },
  };
}
